import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 让右下角悬浮球可拖动（并记住位置）。
 *
 * 为什么需要：AI 助手悬浮球固定在 `bottom-4 right-6`，正好压住页面右下角的内容
 * （实捕：落地页 footer 的「扫码」入口被它盖住）。改成可拖动后用户能把它挪开。
 *
 * 三个要点：
 *  1. **拖动与点击要分开**：位移超过阈值才算拖动，否则按点击处理（否则球再也点不开面板）。
 *  2. **位置持久化**：localStorage，刷新后还在用户放的地方。
 *  3. **夹回可视区**：窗口缩放后位置不能跑到屏幕外。
 */
export interface FabPos {
  x: number;
  y: number;
}

const STORAGE_KEY = 'molore:fab:pos';
const FAB_SIZE = 48; // 与 w-12 h-12 对应
const MARGIN = 8;
const DRAG_THRESHOLD = 5; // px，低于此视为点击

function clampPos(x: number, y: number): FabPos {
  const maxX = Math.max(MARGIN, window.innerWidth - FAB_SIZE - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - FAB_SIZE - MARGIN);
  return {
    x: Math.min(Math.max(MARGIN, x), maxX),
    y: Math.min(Math.max(MARGIN, y), maxY),
  };
}

function loadPos(): FabPos | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as FabPos;
    if (typeof p?.x === 'number' && typeof p?.y === 'number' && Number.isFinite(p.x) && Number.isFinite(p.y)) {
      return clampPos(p.x, p.y);
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function useDraggableFab(onTap: () => void) {
  // null = 用默认的 bottom-4 right-6 定位（未拖动过）
  const [pos, setPos] = useState<FabPos | null>(loadPos);
  const [dragging, setDragging] = useState(false);
  const ref = useRef<HTMLButtonElement | null>(null);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    // 只响应主键（鼠标左键 / 触摸）
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    const rect = ref.current?.getBoundingClientRect();
    const ox = rect ? rect.left : window.innerWidth - FAB_SIZE - 24;
    const oy = rect ? rect.top : window.innerHeight - FAB_SIZE - 16;
    drag.current = { sx: e.clientX, sy: e.clientY, ox, oy, moved: false };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    setPos(clampPos(d.ox + dx, d.oy + dy));
  }, []);

  const endDrag = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const d = drag.current;
      drag.current = null;
      setDragging(false);
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      if (!d) return;
      if (!d.moved) {
        onTap(); // 没位移 = 点击
        return;
      }
      setPos((p) => {
        if (p) {
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
          } catch {
            /* ignore */
          }
        }
        return p;
      });
    },
    [onTap]
  );

  /**
   * 复位到默认右下角。用**右键**而不是双击 —— 双击的第一下（无位移）会触发 onTap
   * 打开对话面板，球随即被 `{!showChatPanel && …}` 卸载，第二下必然落空，
   * 所以「双击复位」在设计上就不可能生效（本文件首版实捕）。
   */
  const reset = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setPos(null);
  }, []);

  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampPos(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** 有 pos 时用内联 left/top 覆盖默认定位 */
  const style: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto', touchAction: 'none' }
    : { touchAction: 'none' };

  return {
    ref,
    style,
    dragging,
    moved: pos !== null,
    handlers: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, onContextMenu: reset },
  };
}
